/**
 * Insight detail — public /insights/:slug page.
 * Uses server-rendered body_html (already sanitized by markdown2).
 * Includes JSON-LD BlogPosting structured data for Google SEO.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Clock, Tag, Calendar, Share2, ShieldCheck, Brain, Sparkles, Loader2, BookOpen } from 'lucide-react';
import useMetaTags from '../../hooks/useMetaTags';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  ai_act: { label: 'EU AI Act', tone: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30', cta: '/landing/ai-act', ctaLabel: 'Fazer Self-Assessment AI Act', ctaIcon: Brain },
  nis2:   { label: 'NIS2', tone: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', cta: '/landing/nis2', ctaLabel: 'Fazer Self-Assessment NIS2', ctaIcon: ShieldCheck },
  both:   { label: 'NIS2 + AI Act', tone: 'bg-sky-500/15 text-sky-300 border-sky-500/30', cta: '/trust', ctaLabel: 'Descarregar Trust Report', ctaIcon: Sparkles },
};

export default function InsightDetail() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/insights/${slug}`);
        if (mounted) setState({ loading: false, data, error: null });
      } catch (err) {
        if (mounted) setState({ loading: false, data: null, error: err?.response?.data?.detail || 'Artigo não encontrado.' });
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  useMetaTags({
    title: state.data ? `${state.data.title} · NIS2AI Insights` : 'Insight · NIS2AI',
    description: state.data?.excerpt,
    type: 'article',
  });

  // Inject JSON-LD structured data for Google
  useEffect(() => {
    if (!state.data) return;
    const d = state.data;
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: d.title,
      description: d.excerpt,
      author: { '@type': 'Organization', name: d.author || 'NIS2AI Editorial' },
      datePublished: d.published_at,
      dateModified: d.updated_at,
      publisher: { '@type': 'Organization', name: 'NIS2AI', logo: { '@type': 'ImageObject', url: `${window.location.origin}/logo.png` } },
      mainEntityOfPage: { '@type': 'WebPage', '@id': window.location.href },
      keywords: (d.tags || []).join(', '),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, [state.data]);

  const share = () => {
    if (navigator.share) {
      navigator.share({ title: state.data?.title, url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copiado.');
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#080d18] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!state.data) {
    return (
      <div className="min-h-screen bg-[#080d18] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <BookOpen className="h-10 w-10 mx-auto text-slate-600 mb-3" />
          <h1 className="text-2xl font-semibold text-slate-100">Artigo não encontrado</h1>
          <p className="text-sm text-slate-500 mt-2">{state.error}</p>
          <Link to="/insights" className="mt-6 inline-flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300">
            <ArrowLeft className="h-4 w-4" /> Voltar aos insights
          </Link>
        </div>
      </div>
    );
  }

  const d = state.data;
  const cat = CATEGORY_META[d.category] || CATEGORY_META.both;
  const CtaIcon = cat.ctaIcon;
  const published = d.published_at ? new Date(d.published_at).toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-[#080d18] text-slate-100 relative">
      <div className="pointer-events-none fixed inset-0 opacity-25">
        <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 h-[420px] w-[900px] rounded-full bg-indigo-600/20 blur-[130px]" />
      </div>

      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5 border-b border-white/5">
        <Link to="/insights" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="insight-back-link">
          <ArrowLeft className="h-4 w-4" /> Insights
        </Link>
        <div className="flex items-center gap-3">
          <button onClick={share} className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1" data-testid="insight-share-btn">
            <Share2 className="h-3.5 w-3.5" /> Partilhar
          </button>
          <Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors">Entrar</Link>
        </div>
      </nav>

      <article className="relative z-10 max-w-3xl mx-auto px-6 md:px-8 py-14 md:py-20" data-testid="insight-article">
        <header className="mb-10">
          <div className="flex items-center gap-3 mb-6">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] uppercase tracking-widest font-semibold border ${cat.tone}`} data-testid="insight-category-badge">
              {cat.label}
            </span>
            <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Clock className="h-3 w-3" />{d.read_time_min || 3} min de leitura</span>
            {published && (
              <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{published}</span>
            )}
          </div>
          <div className="text-5xl mb-4">{d.hero_emoji || '📄'}</div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]" data-testid="insight-title">
            {d.title}
          </h1>
          <p className="mt-5 text-lg text-slate-300 leading-relaxed" data-testid="insight-excerpt">{d.excerpt}</p>
        </header>

        {/* Body — rendered from markdown2 */}
        <div
          className="insight-body prose prose-invert prose-slate max-w-none prose-headings:font-semibold prose-headings:tracking-tight prose-a:text-indigo-400 hover:prose-a:text-indigo-300 prose-strong:text-slate-100 prose-code:text-emerald-300 prose-code:bg-slate-800/60 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:before:content-[''] prose-code:after:content-[''] prose-blockquote:border-l-indigo-500 prose-li:text-slate-300"
          dangerouslySetInnerHTML={{ __html: d.body_html }}
          data-testid="insight-body-html"
        />

        {/* Tags */}
        {d.tags?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-white/5 flex items-center gap-2 flex-wrap">
            <Tag className="h-3.5 w-3.5 text-slate-500" />
            {d.tags.map(t => (
              <span key={t} className="text-[11px] text-slate-400 bg-white/5 px-2 py-0.5 rounded-full">{t}</span>
            ))}
          </div>
        )}

        {/* CTA */}
        <section className="mt-14 p-8 rounded-2xl border border-white/10 bg-gradient-to-br from-indigo-500/10 to-sky-500/10 backdrop-blur-md text-center" data-testid="insight-cta">
          <Sparkles className="h-6 w-6 mx-auto text-indigo-400 mb-2" />
          <h3 className="text-xl font-semibold">Aplicar isto na tua organização?</h3>
          <p className="mt-2 text-sm text-slate-300 max-w-lg mx-auto">
            Faz um self-assessment gratuito e recebe um plano personalizado gerado por IA.
          </p>
          <Link to={cat.cta} className="mt-5 inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-100 rounded-full font-semibold h-11 px-6 text-sm" data-testid="insight-cta-btn">
            <CtaIcon className="h-4 w-4" /> {cat.ctaLabel} <ArrowRight className="h-4 w-4" />
          </Link>
        </section>

        <div className="mt-8 text-center text-xs text-slate-500">
          Visualizações: {d.views_count}
        </div>
      </article>

      <footer className="relative z-10 border-t border-white/5 py-8 px-6 text-center text-xs text-slate-500">
        <p>
          © {new Date().getFullYear()} NIS2AI · <Link to="/insights" className="hover:text-white underline underline-offset-2">Mais insights</Link>
          {' · '}<Link to="/trust" className="hover:text-white underline underline-offset-2">Trust Center</Link>
        </p>
      </footer>
    </div>
  );
}
